// We are making an exception since this package has no other dependencies
import { BigNumber as BigNumberJs } from "npm:bignumber.js";

export type BigNumeric = string | undefined;

export class BigNumber extends BigNumberJs {
    static {
        this.config({ EXPONENTIAL_AT: 1e9 });
    }

    constructor(value: BigNumeric) {
        super(value ?? NaN);
    }
}

export interface Range {
    min?: string;
    max?: string;
}

class DecimalMath {
    static isNaN(value: BigNumeric): boolean {
        return new BigNumber(value).isNaN();
    }

    static trunc(value: BigNumeric, decimals: number): string {
        if (decimals < 0) {
            throw new Error("Decimal places must be non-negative");
        }
        const num = new BigNumber(value);
        if (!num.isFinite()) {
            return "NaN";
        }
        return num.decimalPlaces(decimals, BigNumber.ROUND_DOWN).toString();
    }

    static ceil(value: BigNumeric, decimals: number): string {
        if (decimals < 0) {
            throw new Error("Decimal places must be non-negative");
        }
        const num = new BigNumber(value);
        if (!num.isFinite()) {
            return "NaN";
        }
        return num.decimalPlaces(decimals, BigNumber.ROUND_CEIL).toString();
    }

    static round(value: BigNumeric, decimals: number): string {
        const num = new BigNumber(value);
        return num.toFixed(decimals, BigNumber.ROUND_HALF_UP);
    }

    static add(...values: BigNumeric[]): string {
        const [start, ...nums] = values;
        return nums.reduce(
            (diff, val) => diff.plus(new BigNumber(val)),
            new BigNumber(start),
        ).toString();
    }

    static sub(...values: BigNumeric[]): string {
        const [start, ...nums] = values;
        return nums.reduce(
            (diff, val) => diff.minus(new BigNumber(val)),
            new BigNumber(start),
        ).toString();
    }

    static mul(...values: BigNumeric[]): string {
        const [start, ...nums] = values;
        return nums.reduce(
            (product, val) => product.multipliedBy(new BigNumber(val)),
            new BigNumber(start),
        ).toString();
    }

    static div(...values: BigNumeric[]): string {
        const [start, ...nums] = values;
        const quotient = nums.reduce(
            (quotient, val) => {
                return quotient.dividedBy(new BigNumber(val));
            },
            new BigNumber(start),
        );
        return quotient.isFinite() ? quotient.toString() : "NaN";
    }

    static lt(a: BigNumeric, b: BigNumeric): boolean {
        return new BigNumber(a).isLessThan(b ?? NaN);
    }
    static gt(a: BigNumeric, b: BigNumeric): boolean {
        return new BigNumber(a).isGreaterThan(b ?? NaN);
    }

    static lte(a: BigNumeric, b: BigNumeric): boolean {
        return this.lt(a, b) || this.eq(a, b);
    }

    static gte(a: BigNumeric, b: BigNumeric): boolean {
        return this.gt(a, b) || this.eq(a, b);
    }

    static min(a: BigNumeric, b: BigNumeric): BigNumeric {
        return this.lt(a, b) ? a : (this.isNaN(b) ? a : b);
    }

    static max(a: BigNumeric, b: BigNumeric): BigNumeric {
        return this.gt(a, b) ? a : (this.isNaN(b) ? a : b);
    }

    static eq(a: BigNumeric, b: BigNumeric): boolean {
        return new BigNumber(a).isEqualTo(b ?? NaN);
    }

    static intersectRange(a: Range, b: Range): Range {
        return {
            min: this.max(a.min, b.min),
            max: this.min(a.max, b.max),
        };
    }

    static emptyRange(limits: Range): boolean {
        return this.gt(limits.min, limits.max);
    }

    static belowRange(limits: Range, value: BigNumeric): boolean {
        return DM.lt(value, limits.min);
    }

    static aboveRange(limits: Range, value: BigNumeric): boolean {
        return this.gt(value, limits.max);
    }

    static withinRange(limits: Range, value: BigNumeric): boolean {
        return !this.belowRange(limits, value) && !this.aboveRange(limits, value);
    }

    static mergeIntervals(ranges: Range[], tolerance: string = "0"): Range[] {
        const sorted = ranges
            .filter((r) => !this.emptyRange(r))
            .map((r) => ({ min: r.min ?? "-Infinity", max: r.max ?? "Infinity" }))
            .sort((a, b) => this.lt(a.min, b.min) ? -1 : this.gt(a.min, b.min) ? 1 : 0);
        if (sorted.length === 0) return [];
        const merged = sorted.slice(1).reduce((acc, curr) => {
            const last = acc[acc.length - 1];
            if (this.lte(curr.min, this.add(last.max, tolerance))) {
                last.max = this.max(curr.max, last.max) as string;
            } else {
                acc.push({ ...curr });
            }
            return acc;
        }, [{ ...sorted[0] }]);
        return merged.map(({ min, max }) => ({
            ...(min !== "-Infinity" && { min }),
            ...(max !== "Infinity" && { max }),
        }));
    }
}

export const DM = DecimalMath;
